import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import BackButton from "../components/common/BackButton";

// --- Mock Data (Fallback / Default) ---
const defaultProductData = {
    id: "PE-001",
    title: "The Vow - Wedding Hoop",
    price: "₹ 2,499",
    description: "A timeless keepsake capturing the warmth of your special day. Hand-stitched on premium organic cotton with silk threads, this hoop art preserves your favorite wedding portrait in intricate detail.",
    category: "Wedding Collection",
    image: "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=1200&auto=format&fit=crop",
    highlights: [
        "Handcrafted with Silk & Cotton Threads",
        "10-inch Wooden Bamboo Hoop",
        "Customizable Names & Date",
        "3D Embossed Texture"
    ]
};

export default function ProductView() {
    const location = useLocation();
    const productFromState = location.state?.product;

    const productData = productFromState ? {
        ...productFromState,
        image: productFromState.imageBase, // Map imageBase to image
        highlights: productFromState.highlights || defaultProductData.highlights // Use passed highlights or default
    } : defaultProductData;



    const [activeTab, setActiveTab] = useState(null); // For accordions

    // Check Flags
    const isPhotoEmbroidery = productData.id?.startsWith("PE-");
    const isHoopEmbroidery = productData.id?.startsWith("HE-");
    const hasSizeOptions = isPhotoEmbroidery || isHoopEmbroidery;

    const pricingConfig = {
        // Photo Embroidery configuration
        "PE-01": { "5x7": "₹ 1,999", "6x4": "₹ 2,799", "8x10": "₹ 3,499", "10x12": "₹ 3,999" },
        "PE-02": { "5x7": "₹ 2,499", "6x4": "₹ 3,199", "8x10": "₹ 3,899", "10x12": "₹ 4,399" },
        "PE-03": { "5x7": "₹ 3,999", "6x4": "₹ 4,799" },
        "PE-04": { "5x7": "₹ 1,499", "6x4": "₹ 1,799" },
        "PE-05": { "6x4": "₹ 4,999" },
        "PE-06": { "5x7": "₹ 1,499", "6x4": "₹ 1,799", "8x10": "₹ 1,999" },
        "PE-07": { "6x4": "₹ 1,299", "8x10": "₹ 1,799" },

        // Hoop Embroidery configuration
        "HE-01": { '8"': "₹ 2,199", '10"': "₹ 2,699" },
        "HE-02": { '8"': "₹ 2,799", '10"': "₹ 3,299" },
        "HE-03": { '8"': "₹ 3,499", '10"': "₹ 3,999" },
        "HE-04": { '8"': "₹ 3,499", '10"': "₹ 3,999" },
    };

    // Generic fallback if matched ID doesn't have an entry
    const defaultPricing = isPhotoEmbroidery
        ? { "5x7": "₹ 2,499", "6x4": "₹ 2,499", "8x10": "₹ 3,499", "10x12": "₹ 4,499" }
        : isHoopEmbroidery ? { '8"': "₹ 2,499", '10"': "₹ 2,999" } : {};

    const currentProductPricing = pricingConfig[productData.id] || defaultPricing;
    const availableSizes = Object.keys(currentProductPricing);

    // Initial Selection
    const [selectedSize, setSelectedSize] = useState(() => {
        if (availableSizes.length > 0) {
            return availableSizes.includes("5x7") ? "5x7" : availableSizes.includes('8"') ? '8"' : availableSizes[0];
        }
        return null;
    });

    // We also need to update selectedSize if the product changes
    React.useEffect(() => {
        const newAvailableSizes = Object.keys(pricingConfig[productData.id] || defaultPricing);
        if (newAvailableSizes.length > 0 && !newAvailableSizes.includes(selectedSize)) {
            setSelectedSize(
                newAvailableSizes.includes("5x7") ? "5x7" :
                    newAvailableSizes.includes('8"') ? '8"' :
                        newAvailableSizes[0]
            );
        }
    }, [productData.id]);


    const currentPrice = hasSizeOptions ? (currentProductPricing[selectedSize] || productData.price) : productData.price;

    const toggleTab = (index) => {
        setActiveTab(activeTab === index ? null : index);
    };

    const handleWhatsAppOrder = () => {
        const phoneNumber = "918897388513"; // Replace with your number

        let message = `Hi Bandham, I would like to order:
    
Product ID: ${productData.id}
Price: ${currentPrice}`;

        if (hasSizeOptions && selectedSize) {
            message += `\nSize: ${selectedSize}`;
        }

        message += `\n\n\nPlease let me know the customization options!`;

        const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank");
    };

    return (
        <section className="pv-section">
            <div className="pv-container">

                <div className="pv-back-wrapper" style={{ gridColumn: '1 / -1' }}>
                    <BackButton />
                </div>

                {/* --- Left Column: The Art Frame --- */}
                <div className="pv-image-col">
                    <div className="pv-image-wrapper">
                        <img
                            src={productData.image}
                            alt={productData.title}
                            className="pv-main-image"
                        />
                        {/* Texture Overlay for 'Canvas' feel */}
                        <div className="pv-texture-overlay"></div>
                    </div>
                </div>

                {/* --- Right Column: The Details --- */}
                <div className="pv-details-col">

                    <div className="pv-breadcrumb">
                        PRODUCT ID: <span className="highlight">{productData.id}</span>
                    </div>

                    <h1 className="pv-title">{productData.title}</h1>
                    <div className="pv-price">{currentPrice}</div>

                    <div className="pv-divider"></div>

                    {hasSizeOptions && availableSizes.length > 0 && (
                        <div className="pv-size-selector-wrapper">
                            <label className="pv-size-label">Select Size:</label>
                            <div className="pv-size-options">
                                {availableSizes.map((size) => (
                                    <button
                                        key={size}
                                        className={`pv-size-btn ${selectedSize === size ? "active" : ""}`}
                                        onClick={() => setSelectedSize(size)}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                            <p className="pv-size-note">
                                {isPhotoEmbroidery ? (
                                    <><strong>Note:</strong> Design will vary according to your image and its background. Do send good quality image with clean background.</>
                                ) : (
                                    <><strong>Note:</strong> Please provide names and specific dates for hoop customizations during checkout on WhatsApp.</>
                                )}
                            </p>
                        </div>
                    )}

                    <p className="pv-description">
                        {productData.description}
                    </p>

                    <ul className="pv-highlights">
                        {productData.highlights.map((item, index) => (
                            <li key={index}>✦ {item}</li>
                        ))}
                    </ul>

                    {/* Quantity & Action */}
                    <div className="pv-action-area">


                        <button className="pv-wa-btn" onClick={handleWhatsAppOrder}>
                            <span className="icon">
                                <i className="fab fa-whatsapp"></i> {/* Ensure FontAwesome is loaded */}
                            </span>
                            Order on WhatsApp
                        </button>
                    </div>

                    {/* Accordions for extra info */}
                    <div className="pv-accordions">
                        <div className={`pv-acc-item ${activeTab === 0 ? "active" : ""}`}>
                            <button className="pv-acc-header" onClick={() => toggleTab(0)}>
                                <span>Material & Care</span>
                                <span className="pv-acc-icon">+</span>
                            </button>
                            <div className="pv-acc-body">
                                <p>Keep away from direct sunlight to prevent fading. Dust lightly with a soft brush. Do not wash.</p>
                            </div>
                        </div>

                        <div className={`pv-acc-item ${activeTab === 1 ? "active" : ""}`}>
                            <button className="pv-acc-header" onClick={() => toggleTab(1)}>
                                <span>Shipping & Returns</span>
                                <span className="pv-acc-icon">+</span>
                            </button>
                            <div className="pv-acc-body">
                                <p>Each piece is made to order. Due to the custom nature, returns are not accepted unless damaged.</p>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </section>
    );
}